import React, { useState } from "react";
import "./style.scss";
import {
  addDays,
  format,
  isSameDay,
  startOfDay,
  differenceInCalendarDays,
} from "date-fns";
import { pl } from "date-fns/locale";
import EACalendarModal from "../../../shared/CalendarModal/EACalendarModal";
import ExerciseFltersModal from "../ExerciseFilters/ExerciseFltersModal";
import { useMediaQuery } from "@material-ui/core";
import useAdditionalHooks, { ExerciseFiltersObject } from "../../hooks/useAdditionalHooks";
import { MdOutlineFilterList, MdKeyboardArrowDown, MdOutlineClear } from "react-icons/md";
import { useTranslation } from "react-i18next";
import EAIconButton from "../../../shared/Buttons/EAIconButton";

interface IProps {
  dateToState: Date;
  dateFromStateMobile: Date;
  dateToStateMobile: Date;
  filters: ExerciseFiltersObject;
  setFilters: (filters: ExerciseFiltersObject) => void;
  onDesktopDateChange: (date: Date) => void;
  onMobileDateChange: (date: Date) => void;
  daysAhead?: number; // ile dni do przodu generujemy (domyślnie 60)
  pageSize?: number; // krok strzałek (domyślnie 7)
  dateFromState: Date; // zewnętrzny wybór dnia
}

const DOW_ABBR = ["ND", "PN", "WT", "ŚR", "CZ", "PT", "SB"]; // JS: 0=ND

const MobileExerciseChoice: React.FC<IProps> = ({
  dateToState,
  dateFromStateMobile,
  dateToStateMobile,
  filters,
  setFilters,
  onMobileDateChange,
  onDesktopDateChange,
  daysAhead = 60,
  pageSize = 3,
  dateFromState,
}) => {
  const today = startOfDay(new Date());
  const [selectedDate, setSelectedDate] = React.useState<Date>(today);

  // wygenerowane dni: dziś + N do przodu
  const days = React.useMemo(
    () => Array.from({ length: daysAhead + 1 }, (_, i) => startOfDay(addDays(today, i))),
    [today, daysAhead]
  );

  // refs do scrolla i chipów
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const chipRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());
  const setChipRef = (date: Date) => (el: HTMLButtonElement | null) => {
    const key = date.toISOString();
    if (!el) chipRefs.current.delete(key);
    else chipRefs.current.set(key, el);
  };

  const scrollIntoViewIfNeeded = (date: Date) => {
    const key = date.toISOString();
    const chip = chipRefs.current.get(key);
    const scroller = scrollRef.current;
    if (!chip || !scroller) return;
    const chipRect = chip.getBoundingClientRect();
    const scRect = scroller.getBoundingClientRect();
    const isVisible = chipRect.left >= scRect.left && chipRect.right <= scRect.right;
    if (!isVisible) {
      chip.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  };

  const handleSelect = (date: Date) => {
    setSelectedDate(date);
    onMobileDateChange(date);
    scrollIntoViewIfNeeded(date);
  };

  // const pageScroll = (dir: -1 | 1) => {
  //   const scroller = scrollRef.current;
  //   if (!scroller) return;
  //   const chipWidth = 74; // przybliżona szerokość „chipa”
  //   scroller.scrollBy({ left: dir * pageSize * chipWidth, behavior: "smooth" });
  // };

  // 🔹 Reakcja na zewnętrzny props dateFromState
  React.useEffect(() => {
    if (!dateFromState) return;

    const parsed = dateFromState instanceof Date ? dateFromState : new Date(dateFromState);
    if (isNaN(parsed.getTime())) return; // niepoprawna data — ignorujemy

    const next = startOfDay(parsed);

    // Clamp do zakresu (dzis .. ostatni dzień listy), żeby nie wyjść poza wygenerowane dni
    const last = days[days.length - 1];
    const clamped = next < today ? today : next > last ? last : next;

    setSelectedDate(clamped);

    // przewiń, żeby było widać
    if (isSameDay(clamped, today)) {
      scrollRef.current?.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      scrollIntoViewIfNeeded(clamped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFromState, days.length]);

  // klawiatura ← →
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.preventDefault();
      const delta = e.key === "ArrowLeft" ? -1 : 1;
      const idx = differenceInCalendarDays(selectedDate, today);
      const nextIdx = Math.max(0, Math.min(days.length - 1, idx + delta));
      handleSelect(days[nextIdx]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, days]);

  const isTodaySelected = isSameDay(selectedDate, today);
  const monthLabel = format(selectedDate, "LLLL", { locale: pl });
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1100px)");
  const { t } = useTranslation();
  const { isSomeFiltersSelected, handleResetFilters } = useAdditionalHooks(filters, setFilters);

  const [openCalendar, setOpenCalendar] = useState(false);
  const handleOpenCalendar = () => {
    setOpenCalendar(true);
  };

  const handleCloseCalendar = () => {
    setOpenCalendar(false);
  };
  const handleDateChange = (date: Date) => {
    if (isDesktop) {
      onDesktopDateChange(date);
    } else {
      onMobileDateChange(date);
    }
    setOpenCalendar(false);
  };

  return (
    <div className="mexc">
      {/* Header: miesiąc + chevron (UI) */}
      <div className="mexc__header">
        <div className="mexc__header-month" onClick={handleOpenCalendar}>
          <p className="body-text-large text-color-dark text-bold flex-start-center">
            {monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)} <MdKeyboardArrowDown />
          </p>
        </div>

        <div className="mexc__header-buttons">
          <EAIconButton
            title={isSomeFiltersSelected() ? t("wybrano_filtry") : t("filtry")}
            onClick={() => setOpen(true)}
            icon={<MdOutlineFilterList />}
            variant={isSomeFiltersSelected() ? "filled" : "outlined"}
          />
          {isSomeFiltersSelected() && (
            <EAIconButton
              title={t("wyczysc_filtry")}
              onClick={handleResetFilters}
              icon={<MdOutlineClear />}
              variant="icon"
            />
          )}
        </div>
      </div>

      {/* Row: DZIŚ + scroller */}
      <div className="mexc__row">
        {/* Sticky DZIŚ */}
        <div className="mexc__today">
          <button
            className={["mexc__chip", isTodaySelected ? "is-active" : "is-today"].join(" ")}
            onClick={() => {
              handleSelect(today);
              scrollRef.current?.scrollTo({ left: 0, behavior: "smooth" });
            }}
            role="tab"
            aria-selected={isTodaySelected}
            aria-label={`${format(today, "EEEE", { locale: pl })}, ${format(today, "d.MM", { locale: pl })} (Dziś)`}
          >
            <span className="mexc__dow">{DOW_ABBR[today.getDay()]}</span>
            <span className="mexc__date">DZIŚ</span>
          </button>
        </div>

        {/* Scroller od jutra */}
        <div className="mexc__scroller" role="tablist" aria-label="Wybierz dzień" ref={scrollRef}>
          {days.map((date, i) => {
            if (i === 0) return null; // DZIŚ już mamy po lewej
            const active = isSameDay(selectedDate, date);
            const labelDate = i === 1 ? format(date, "d", { locale: pl }) : format(date, "d", { locale: pl });
            const dow = DOW_ABBR[date.getDay()];
            const aria = `${format(date, "EEEE", { locale: pl })}, ${format(date, "d.MM", { locale: pl })}`;

            return (
              <button
                key={date.toISOString()}
                ref={setChipRef(date)}
                role="tab"
                aria-selected={active}
                aria-label={aria}
                className={["mexc__chip", active ? "is-active" : ""].join(" ")}
                onClick={() => handleSelect(date)}
              >
                <span className="mexc__dow">{dow}</span>
                <span className="mexc__date">{labelDate}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Fade edges (hint scroll) */}
      <div className="mexc__edges" aria-hidden="true" />

      {open && (
        <ExerciseFltersModal
          open={open}
          setOpen={setOpen}
          dateFromState={isDesktop ? dateFromState : dateFromStateMobile}
          dateToState={isDesktop ? dateToState : dateToStateMobile}
          filters={filters}
          setFilters={setFilters}
        />
      )}

      {openCalendar && <EACalendarModal onClose={handleCloseCalendar} onDatePick={handleDateChange} />}
    </div>
  );
};

export default MobileExerciseChoice;
